import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";

export const RXDB_EPOCUS_REFERRAL_COLLECTION_NAME = "epocus-referral";

export const useRxCollectionEpocusReferral =
  (): RxCollection<EpocusReferral> | null =>
    useRxCollection<EpocusReferral>(RXDB_EPOCUS_REFERRAL_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<EpocusReferral>>;
export type EpocusReferralQueryConstructor = UseRxData["1"];

export type EpocusReferralUrgency = "routine" | "urgent" | "emergency";
export type EpocusReferralStatus = "sent" | "acknowledged" | "outcomeRecorded";

const EPOCUS_REFERRAL_SCHEMA_LITERAL = {
  title: "epocus referral schema",
  version: 1,
  description: "Represents a clinical referral created from a POCUS encounter",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    encounterId: {
      type: "string",
      maxLength: 100,
    },
    examId: {
      type: ["string", "null"],
      maxLength: 100,
    },
    referringUserId: {
      type: "string",
      maxLength: 256,
    },
    receivingUserId: {
      type: "string",
      maxLength: 256,
    },
    urgency: {
      type: "string",
      enum: ["routine", "urgent", "emergency"],
      maxLength: 20,
    },
    clinicalReason: {
      type: "string",
    },
    status: {
      type: "string",
      enum: ["sent", "acknowledged", "outcomeRecorded"],
      maxLength: 20,
    },
    createdAt: {
      type: "string",
      format: "date-time",
      maxLength: 30,
    },
    sentAt: {
      type: "string",
      format: "date-time",
      maxLength: 30,
    },
    acknowledgedAt: {
      type: ["string", "null"],
      format: "date-time",
      maxLength: 30,
    },
    outcomeRecordedAt: {
      type: ["string", "null"],
      format: "date-time",
      maxLength: 30,
    },
  },
  required: [
    "id",
    "encounterId",
    "referringUserId",
    "receivingUserId",
    "urgency",
    "clinicalReason",
    "status",
    "createdAt",
    "sentAt",
  ],
  indexes: [["receivingUserId", "status"], ["encounterId"]],

  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(EPOCUS_REFERRAL_SCHEMA_LITERAL);

export type EpocusReferral = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const EPOCUS_REFERRAL_SCHEMA: RxJsonSchema<EpocusReferral> =
  EPOCUS_REFERRAL_SCHEMA_LITERAL;
